import { useEffect, useState } from "react";
import arabicFlag from "./assets/flags/sa.svg";
import englishFlag from "./assets/flags/gb.svg";
import italianFlag from "./assets/flags/it.svg";
import turkishFlag from "./assets/flags/tr.svg";

import {
  getReleasedTargetLanguagesForUi,
  getUiLanguageDirection,
  isReleasedLanguagePair,
  type UiDirection,
} from "@mobile/onboarding/languageSupport";

export type SupportLanguage = "en" | "tr" | "ar";
export type TargetLanguage = "en" | "tr" | "ar" | "it";
export type EnglishAccent = "us" | "uk";
export type Appearance = "dark" | "light" | "atelier";
export type AppRoute = "home" | "practice" | "vocabulary" | "missions" | "mixed" | "profile";

export interface LearnerSetup {
  supportLanguage: SupportLanguage;
  targetLanguage: TargetLanguage;
  englishAccent?: EnglishAccent;
}

export interface LanguageMeta {
  code: SupportLanguage | TargetLanguage;
  flag: string;
  flagImage: string;
  nativeName: string;
  englishName: string;
  locale: string;
}

export const LANGUAGE_META: Record<string, LanguageMeta> = {
  ar: { code: "ar", flag: "🇸🇦", flagImage: arabicFlag, nativeName: "العربية", englishName: "Arabic", locale: "ar-SA" },
  en: { code: "en", flag: "🇬🇧", flagImage: englishFlag, nativeName: "English", englishName: "English", locale: "en-US" },
  it: { code: "it", flag: "🇮🇹", flagImage: italianFlag, nativeName: "Italiano", englishName: "Italian", locale: "it-IT" },
  tr: { code: "tr", flag: "🇹🇷", flagImage: turkishFlag, nativeName: "Türkçe", englishName: "Turkish", locale: "tr-TR" },
};

export const SUPPORT_LANGUAGES: SupportLanguage[] = ["en", "tr", "ar"];

export function targetLanguagesForSupport(language: SupportLanguage): TargetLanguage[] {
  return getReleasedTargetLanguagesForUi(language)
    .filter((value): value is TargetLanguage => value in LANGUAGE_META);
}

export function isValidSetup(value: unknown): value is LearnerSetup {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Partial<LearnerSetup>;
  return SUPPORT_LANGUAGES.includes(candidate.supportLanguage as SupportLanguage)
    && Boolean(candidate.targetLanguage && candidate.targetLanguage in LANGUAGE_META)
    && isReleasedLanguagePair(candidate.supportLanguage, candidate.targetLanguage);
}

export function normalizeLearnerSetup(value: unknown): LearnerSetup | null {
  if (!isValidSetup(value)) {
    return null;
  }
  return {
    supportLanguage: value.supportLanguage,
    targetLanguage: value.targetLanguage,
    englishAccent: "us",
  };
}

export function speechLocaleForSetup(setup: LearnerSetup): string {
  if (setup.targetLanguage === "en") {
    return "en-US";
  }
  return LANGUAGE_META[setup.targetLanguage].locale;
}

export function directionForSupport(language: SupportLanguage): UiDirection {
  return getUiLanguageDirection(language);
}

export const WEB_COPY = {
  en: {
    browserSpeechUnavailable: "Voice practice is not supported by this browser yet",
    browserMicrophoneMissing: "No usable microphone was found. Connect a microphone, then refresh this page",
    browserMicrophonePermissionDenied: "Microphone access is blocked. Allow it in your browser's site settings, then try again",
    browserMicrophoneUnavailable: "Your microphone is connected but cannot be used right now. Close other apps using it, then try again",
    accountDelete: "Delete account",
    accountDeleteConfirm: "This permanently deletes your Wordinarius Echo account and learning data. Continue?",
    accountGatewayBody: "Keep your progress and mobile Premium access connected on every device",
    accountGatewayTitle: "Continue with your account",
    accountFree: "Free with rewarded access",
    accountPremium: "Premium from mobile store",
    accountSignedOut: "Sign in to use protected lessons and sync your progress",
    appleSignIn: "Continue with Apple",
    check: "Check",
    chooseTarget: "Choose your practice language",
    chooseStudyArea: "Choose a learning area",
    chooseScenarioBeforePractice: "Choose a level, learning area, and scenario first. Your practice options will open as soon as the scenario is ready.",
    close: "Close",
    correct: "Correct",
    contentLoadFailed: "Content could not be loaded",
    home: "Learn",
    googleSignIn: "Continue with Google",
    listenRepeat: "Listen & repeat",
    listenWrite: "Listen & write",
    loadingContent: "Preparing your practice",
    levelNames: { a1: "Beginner", a2: "Elementary", b1: "Intermediate", b2: "Upper intermediate" },
    missions: "Missions",
    mixed: "Mixed missions",
    next: "Next",
    profile: "Profile",
    profileAccess: "Access",
    profileAccount: "Account",
    profileLanguage: "Languages",
    profileSettings: "Settings",
    premiumRefresh: "Refresh premium status",
    privacySettings: "Privacy and cookie settings",
    reminderDaily: "Daily",
    reminderFrequency: "Frequency",
    reminderNotificationBody: "Continue the language practice you left open",
    reminderNotificationTitle: "Your practice is ready",
    reminders: "Reminders",
    remindersDisable: "Turn off reminders",
    remindersEnable: "Turn on reminders",
    remindersOff: "Off",
    remindersOn: "On",
    reminderTime: "Time",
    reminderWeekly: "Weekly",
    practice: "Sentence practice",
    resetSetup: "Change languages",
    start: "Start",
    stop: "Stop",
    signOut: "Sign out",
    tapLetters: "Build the word",
    theme: "Appearance",
    transcript: "I heard",
    translate: "Translate",
    tryAgain: "Try again",
    unscramble: "Unscramble",
    vocabulary: "Vocabulary",
    vocabularyComplete: "Vocabulary practice complete",
    vocabularyCompleteHint: "You reached the end of this word set. Replay it or choose another exercise.",
    vocabularyReplay: "Replay word set",
    missionsComplete: "Mission round complete",
    missionsReplay: "Replay missions",
    webAppTitle: "Speaking workspace",
    yourTurn: "Your turn",
  },
  tr: {
    browserSpeechUnavailable: "Bu tarayıcı henüz sesli pratiği desteklemiyor",
    browserMicrophoneMissing: "Bu cihazda kullanılabilir bir mikrofon bulunamadı. Bir mikrofon bağlayıp sayfayı yenile",
    browserMicrophonePermissionDenied: "Mikrofon erişimi engellenmiş. Tarayıcının site ayarlarından izin verip tekrar dene",
    browserMicrophoneUnavailable: "Mikrofon bağlı ancak şu anda kullanılamıyor. Mikrofonu kullanan diğer uygulamaları kapatıp tekrar dene",
    accountDelete: "Hesabı sil",
    accountDeleteConfirm: "Wordinarius Echo hesabın ve öğrenme verilerin kalıcı olarak silinecek. Devam edilsin mi?",
    accountGatewayBody: "İlerlemen ve mobil Premium erişimin tüm cihazlarında aynı hesaba bağlı kalsın",
    accountGatewayTitle: "Hesabınla devam et",
    accountFree: "Reklamlı ücretsiz erişim",
    accountPremium: "Mobil mağazadan Premium",
    accountSignedOut: "Korumalı dersleri kullanmak ve ilerlemeni eşitlemek için giriş yap",
    appleSignIn: "Apple ile devam et",
    check: "Kontrol et",
    chooseTarget: "Pratik yapacağın dili seç",
    chooseStudyArea: "Bir çalışma alanı seç",
    chooseScenarioBeforePractice: "Önce seviye, çalışma alanı ve senaryo seç. Senaryo hazır olduğunda pratik seçeneklerin açılacak.",
    close: "Kapat",
    correct: "Doğru",
    contentLoadFailed: "İçerik yüklenemedi",
    home: "Öğren",
    googleSignIn: "Google ile devam et",
    listenRepeat: "Dinle ve tekrar et",
    listenWrite: "Dinle ve yaz",
    loadingContent: "Pratiğin hazırlanıyor",
    levelNames: { a1: "Başlangıç", a2: "Temel", b1: "Orta", b2: "Orta üstü" },
    missions: "Görevler",
    mixed: "Karışık görevler",
    next: "Sonraki",
    profile: "Profil",
    profileAccess: "Erişim",
    profileAccount: "Hesap",
    profileLanguage: "Diller",
    profileSettings: "Ayarlar",
    premiumRefresh: "Premium durumunu yenile",
    privacySettings: "Gizlilik ve çerez ayarları",
    reminderDaily: "Her gün",
    reminderFrequency: "Sıklık",
    reminderNotificationBody: "Yarım bıraktığın dil pratiğine devam et",
    reminderNotificationTitle: "Pratiğin hazır",
    reminders: "Hatırlatmalar",
    remindersDisable: "Hatırlatmaları kapat",
    remindersEnable: "Hatırlatmaları aç",
    remindersOff: "Kapalı",
    remindersOn: "Açık",
    reminderTime: "Saat",
    reminderWeekly: "Haftada bir",
    practice: "Cümle pratiği",
    resetSetup: "Dilleri değiştir",
    start: "Başla",
    stop: "Durdur",
    signOut: "Çıkış yap",
    tapLetters: "Kelimeyi oluştur",
    theme: "Görünüm",
    transcript: "Duyduğum",
    translate: "Çevir",
    tryAgain: "Tekrar dene",
    unscramble: "Harfleri sırala",
    vocabulary: "Kelime pratiği",
    vocabularyComplete: "Kelime pratiği tamamlandı",
    vocabularyCompleteHint: "Bu kelime grubunun sonuna geldin. Tekrar edebilir veya başka bir egzersiz seçebilirsin.",
    vocabularyReplay: "Kelime grubunu tekrar et",
    missionsComplete: "Görev turu tamamlandı",
    missionsReplay: "Görevleri tekrar et",
    webAppTitle: "Konuşma çalışma alanı",
    yourTurn: "Sıra sende",
  },
  ar: {
    browserSpeechUnavailable: "هذا المتصفح لا يدعم التدريب الصوتي بعد",
    browserMicrophoneMissing: "لم يتم العثور على ميكروفون قابل للاستخدام. صل ميكروفونا ثم أعد تحميل الصفحة",
    browserMicrophonePermissionDenied: "الوصول إلى الميكروفون محظور. اسمح به من إعدادات الموقع في المتصفح ثم حاول مرة أخرى",
    browserMicrophoneUnavailable: "الميكروفون متصل لكنه غير متاح الآن. أغلق التطبيقات الأخرى التي تستخدمه ثم حاول مرة أخرى",
    accountDelete: "حذف الحساب",
    accountDeleteConfirm: "سيتم حذف حساب Wordinarius Echo وبيانات التعلم نهائيا. هل تريد المتابعة؟",
    accountGatewayBody: "احتفظ بتقدمك ووصول Premium من الهاتف مرتبطين بالحساب نفسه على كل أجهزتك",
    accountGatewayTitle: "تابع باستخدام حسابك",
    accountFree: "وصول مجاني مع الإعلانات المكافئة",
    accountPremium: "Premium من متجر الهاتف",
    accountSignedOut: "سجل الدخول لاستخدام الدروس المحمية ومزامنة تقدمك",
    appleSignIn: "المتابعة باستخدام Apple",
    check: "تحقق",
    chooseTarget: "اختر لغة التدريب",
    chooseStudyArea: "اختر مجال التعلم",
    chooseScenarioBeforePractice: "اختر أولا المستوى ومجال التعلم والسيناريو. ستفتح خيارات التدريب فور تجهيز السيناريو.",
    close: "إغلاق",
    correct: "صحيح",
    contentLoadFailed: "تعذر تحميل المحتوى",
    home: "تعلّم",
    googleSignIn: "المتابعة باستخدام Google",
    listenRepeat: "استمع وكرر",
    listenWrite: "استمع واكتب",
    loadingContent: "يتم تجهيز تدريبك",
    levelNames: { a1: "مبتدئ", a2: "أساسي", b1: "متوسط", b2: "فوق المتوسط" },
    missions: "المهمات",
    mixed: "مهمات متنوعة",
    next: "التالي",
    profile: "الملف الشخصي",
    profileAccess: "الوصول",
    profileAccount: "الحساب",
    profileLanguage: "اللغات",
    profileSettings: "الإعدادات",
    premiumRefresh: "تحديث حالة Premium",
    privacySettings: "إعدادات الخصوصية وملفات الارتباط",
    reminderDaily: "يوميا",
    reminderFrequency: "التكرار",
    reminderNotificationBody: "تابع تدريب اللغة الذي تركته",
    reminderNotificationTitle: "تدريبك جاهز",
    reminders: "التذكيرات",
    remindersDisable: "إيقاف التذكيرات",
    remindersEnable: "تشغيل التذكيرات",
    remindersOff: "متوقفة",
    remindersOn: "مفعلة",
    reminderTime: "الوقت",
    reminderWeekly: "أسبوعيا",
    practice: "تدريب الجمل",
    resetSetup: "تغيير اللغات",
    start: "ابدأ",
    stop: "إيقاف",
    signOut: "تسجيل الخروج",
    tapLetters: "كوّن الكلمة",
    theme: "المظهر",
    transcript: "سمعت",
    translate: "ترجمة",
    tryAgain: "حاول مرة أخرى",
    unscramble: "رتب الحروف",
    vocabulary: "تدريب المفردات",
    vocabularyComplete: "اكتمل تدريب المفردات",
    vocabularyCompleteHint: "وصلت إلى نهاية مجموعة الكلمات. يمكنك إعادتها أو اختيار تدريب آخر.",
    vocabularyReplay: "إعادة مجموعة الكلمات",
    missionsComplete: "اكتملت جولة المهام",
    missionsReplay: "إعادة المهام",
    webAppTitle: "مساحة تدريب المحادثة",
    yourTurn: "دورك",
  },
} as const;

const SETUP_KEY = "wordinarius_echo.web.setup.v1";
const APPEARANCE_KEY = "wordinarius_echo.web.appearance.v1";

function readStoredJson<T>(key: string): T | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) as T : null;
  } catch {
    return null;
  }
}

function writeStoredValue(key: string, value: string | null): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // Managed browsers may block persistent storage; the in-memory session remains usable.
  }
}

export function useLearnerSetup(): [LearnerSetup | null, (value: LearnerSetup | null) => void] {
  const [setup, setSetupState] = useState<LearnerSetup | null>(() => {
    const stored = readStoredJson<unknown>(SETUP_KEY);
    return normalizeLearnerSetup(stored);
  });

  useEffect(() => {
    if (setup) {
      writeStoredValue(SETUP_KEY, JSON.stringify(setup));
    }
  }, [setup]);

  const setSetup = (value: LearnerSetup | null) => {
    const normalized = normalizeLearnerSetup(value);
    setSetupState(normalized);
    if (normalized) {
      writeStoredValue(SETUP_KEY, JSON.stringify(normalized));
    } else {
      writeStoredValue(SETUP_KEY, null);
    }
  };

  return [setup, setSetup];
}

export function normalizeAppearance(value: unknown): Appearance {
  return value === "light" || value === "dark" || value === "atelier"
    ? value : "atelier";
}

export function useAppearance(): [Appearance, (value: Appearance) => void] {
  const [appearance, setAppearanceState] = useState<Appearance>(() => {
    if (typeof window !== "undefined") {
      const requestedTheme = new URLSearchParams(window.location.search).get("theme");
      if (requestedTheme === "atelier" || (import.meta.env.DEV && (requestedTheme === "dark" || requestedTheme === "light"))) {
        return requestedTheme;
      }
    }
    try {
      return normalizeAppearance(typeof window === "undefined" ? "" : window.localStorage.getItem(APPEARANCE_KEY));
    } catch {
      return "atelier";
    }
  });

  useEffect(() => {
    document.documentElement.dataset.theme = appearance;
    document.documentElement.dataset.premiumPreview = import.meta.env.DEV
      && new URLSearchParams(window.location.search).get("premium-preview") === "1" ? "true" : "false";
    writeStoredValue(APPEARANCE_KEY, appearance);
  }, [appearance]);

  const setAppearance = (value: Appearance) => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("theme")) {
      url.searchParams.delete("theme");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    }
    setAppearanceState(normalizeAppearance(value));
  };

  return [appearance, setAppearance];
}

const ROUTE_BY_PATH: Record<string, AppRoute> = {
  "/app": "home",
  "/app/": "home",
  "/app/practice": "practice",
  "/app/vocabulary": "vocabulary",
  "/app/missions": "missions",
  "/app/mixed": "mixed",
  "/app/profile": "profile",
};

export function pathForRoute(route: AppRoute): string {
  return route === "home" ? "/app/" : `/app/${route}`;
}

export function useAppRoute(): [AppRoute, (route: AppRoute) => void] {
  const resolveCurrent = () => ROUTE_BY_PATH[window.location.pathname] || "home";
  const [route, setRouteState] = useState<AppRoute>(resolveCurrent);

  useEffect(() => {
    const onPopState = () => setRouteState(resolveCurrent());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const setRoute = (nextRoute: AppRoute) => {
    window.history.pushState({}, "", pathForRoute(nextRoute));
    setRouteState(nextRoute);
  };

  return [route, setRoute];
}
